// Adyvance logo as inline SVG from the paths extracted from logo/Adyvance.pdf (DESIGN_SYSTEM §8,
// D-010: never re-typeset; D-014: the lockup and the dark variant are approved). Variants:
//  - mark: the icon mark (collapsed sidebar, boot indicator; ≥ 24 px in UI)
//  - lockup: horizontal mark + wordmark (top bars; ≥ 24 px tall)
//  - stacked: the full artwork (auth screens; ≥ 120 px wide)
// Tones: color (light surfaces), white (all white, dark/brand fills), dark (colour mark + white
// wordmark on dark surfaces), black (one-colour print). "auto" picks color/dark from the theme.
import { useId } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import {
  LOCKUP_MARK_TRANSFORM,
  MARK_PATHS,
  VIEWBOX,
  WORDMARK_GRADIENT,
  WORDMARK_PATHS,
} from "../brand/paths";

export type LogoVariant = "mark" | "lockup" | "stacked" | "wordmark";
export type LogoTone = "color" | "white" | "dark" | "black";

export interface LogoProps {
  variant?: LogoVariant;
  tone?: LogoTone | "auto";
  /** Rendered height in px (width follows the artwork's proportions). */
  height?: number;
  /** Decorative when a visible "Adyvance" name sits next to it. */
  decorative?: boolean;
  className?: string;
  "data-testid"?: string;
}

const WHITE = "#FFFFFF";
const BLACK = "#000000";

function LogoSvg({
  variant,
  tone,
  height,
  decorative,
  className,
  testId,
}: Required<Pick<LogoProps, "variant" | "height">> & {
  tone: LogoTone;
  decorative?: boolean;
  className?: string;
  testId?: string;
}) {
  const gid = `ady-logo-${useId().replace(/:/g, "")}`;
  const markFill = (c: string) => (tone === "white" ? WHITE : tone === "black" ? BLACK : c);
  const wordFill = tone === "color" ? `url(#${gid})` : tone === "black" ? BLACK : WHITE;
  const showMark = variant !== "wordmark";
  const showWord = variant !== "mark";
  return (
    <svg
      viewBox={VIEWBOX[variant]}
      height={height}
      className={cx("ady-logo w-auto flex-none", className)}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : t("brand.name")}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      data-testid={testId}
    >
      {showWord && tone === "color" ? (
        <defs>
          <linearGradient
            id={gid}
            gradientUnits="userSpaceOnUse"
            x1={WORDMARK_GRADIENT.x1}
            y1={WORDMARK_GRADIENT.y}
            x2={WORDMARK_GRADIENT.x2}
            y2={WORDMARK_GRADIENT.y}
          >
            <stop offset="0" stopColor={MARK_PATHS[0].color} />
            <stop offset="1" stopColor={MARK_PATHS[1].color} />
          </linearGradient>
        </defs>
      ) : null}
      {showMark ? (
        <g transform={variant === "lockup" ? LOCKUP_MARK_TRANSFORM : undefined}>
          {MARK_PATHS.map((p) => (
            <path key={p.color} d={p.d} fill={markFill(p.color)} />
          ))}
        </g>
      ) : null}
      {showWord ? (
        <g fill={wordFill}>
          {WORDMARK_PATHS.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>
      ) : null}
    </svg>
  );
}

export function Logo({
  variant = "lockup",
  tone = "auto",
  height = 28,
  decorative,
  className,
  "data-testid": testId,
}: LogoProps) {
  if (tone !== "auto") {
    return (
      <LogoSvg
        variant={variant}
        tone={tone}
        height={height}
        decorative={decorative}
        className={className}
        testId={testId}
      />
    );
  }
  // Theme-aware: both render and CSS displays one, so only one is in the accessibility tree.
  return (
    <span className={cx("inline-flex", className)} data-testid={testId}>
      <LogoSvg
        variant={variant}
        tone="color"
        height={height}
        decorative={decorative}
        className="ady-logo-light"
      />
      <LogoSvg
        variant={variant}
        tone="dark"
        height={height}
        decorative={decorative}
        className="ady-logo-dark"
      />
    </span>
  );
}
